import type { AxiosError, AxiosRequestConfig } from 'axios'

import type { PROBLEM_CODE } from '@mangovoice/apisauce'

/**
 * A successful API response.
 */
export interface ApiOkResponse<T> {
  ok: true
  problem: null
  originalError: null
  data?: T
  status?: number
  headers?: Record<string, string>
  config?: AxiosRequestConfig
  duration?: number
}

/**
 * A failed API response.
 */
export interface ApiErrorResponse<E = unknown> {
  ok: false
  problem: PROBLEM_CODE
  originalError: AxiosError
  data?: E
  status?: number
  headers?: Record<string, string>
  config?: AxiosRequestConfig
  duration?: number
}

/**
 * Discriminated union representing any API response.
 */
export type ApiResponse<T, E = T> = ApiOkResponse<T> | ApiErrorResponse<E>

/**
 * Standard Mango API error shape.
 */
export interface ApiError {
  type?: string
  status?: number
  code?: string
  message?: string
  parameters?: ParameterErrorInfo[]
}

export interface ParameterErrorInfo {
  name: string
  code: string
  message: string
}

/**
 * Standard paginated API response.
 */
export interface PaginatedApiResult<T> {
  count: number
  next: string | null
  previous: string | null
  results: Array<T>
}

/**
 * Auth data required by the auth interceptor.
 */
export interface ApiClientAuthData {
  accessToken?: string | null
  refreshToken?: string | null
  validationToken?: string | null
  sessionToken?: string | null
}

/**
 * Configuration for the auth interceptor.
 */
export interface AuthInterceptorConfig {
  getAuthData: () => ApiClientAuthData | undefined
  onTokenRefreshed?: (data: unknown) => void
  onAuthFailure?: (error: AuthFailureInfo) => void
  refreshToken: () => Promise<{ ok: boolean; data?: unknown }>
  invalidAuthMessage?: string
}

export interface AuthFailureInfo {
  type:
    | 'invalid_authentication'
    | 'refresh_failed'
    | 'refresh_exception'
    | 'no_auth_data'
  error?: unknown
}

/**
 * Configuration for the rate limit interceptor.
 */
export interface RateLimitInterceptorConfig {
  onRateLimited?: (info: RateLimitInfo) => void
  /** Coalescing window for 429 reports. Defaults to five minutes. */
  coalesceWindowMs?: number
}

export interface RateLimitInfo {
  /** The normalized route this report's 429s came from. */
  url: string
  status: number
  /**
   * Responses on this route that this report stands for. Sum it across
   * reports for the true 429 total — each report carries its own increment,
   * not a running total.
   */
  count: number
}

// ---------------------------------------------------------------------------
// Auth callback types shared across apps
// ---------------------------------------------------------------------------

export enum AuthenticationType {
  GetAppSessionJwts = 'GET_APP_SESSION_JWTS',
  RefreshAppSessionJwts = 'REFRESH_APP_SESSION_JWTS',
  GetSession = 'GET_SESSION',
  InvalidAuthentication = 'INVALID_AUTHENTICATION',
}

export interface AuthenticationResultCallbackData {
  authenticationType: AuthenticationType | string
  ok: boolean
  result: unknown
}

export type AuthenticationResultCallback = (
  data: AuthenticationResultCallbackData,
) => void

export type GetAuthDataCallback = () => ApiClientAuthData | undefined

export interface LoginByEmailPayload {
  username: string
  password: string
}

export interface LoginByEmailResponse {
  access: string
  refresh: string
}

export interface RefreshTokenResponse {
  access: string
  refresh: string
}
